import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import ActiveModel from "../../../models/ActiveModel";
import { getAllActives } from "../../../services/activeService";
import { getSubclassByActive } from "../../../services/subclassService";
import {
    disableProduct,
    enableProduct,
    getActiveProducts,
    getProductsWithFilterNPagination,
    getSubclassProducts,
    ProductsWithFilterNPaginationResponse,
    saveProduct,
} from "../../../services/productService";
import SubclassActiveCompositeModel from "../../../models/SubclassActiveComposite.model";
import { ProductViewModel } from "../../../models/ProductView.model";
import { getDescriptionPeriodByProduct } from "../../../services/periodService";
import { messageErrorConfirmDialog, messageSuccessConfirmDialog, PropsMessageDialog } from "../../../components/message-dialog";
import { ProductModel, ProductWriteModel } from "../../../models/Product.model";
import SubclassModel from "../../../models/SubclassModel";
import { SearchProductModel } from "../../../models/SearchProduct.model";

export const productViewInitial: ProductViewModel = {
    idProduct: undefined,
    idBody: 2,
    idTypeUse: 1,
    description: "",
    active: [],
    subclass: [],
    productName: "",
    mechanismAction: "",
    phProduct: "",
    minimumDosage: "",
    maxDosage: "",
    source: "",
    incompatibility: "",
    pregnant: false,
    vegan: false,
    organic: false,
    period: "",
    showConcentration: true,
};

export const productSchema = Yup.object().shape({
    description: Yup.string().required("Este campo é obrigatório"),
    active: Yup.mixed().test({
        message: "Este campo é obrigatório",
        test: (val) => val.length > 0,
    }),
    productName: Yup.string().required("Este campo é obrigatório"),
    mechanismAction: Yup.string().required("Este campo é obrigatório"),
    phProduct: Yup.string().required("Este campo é obrigatório"),
    minimumDosage: Yup.string().when("showConcentration", {
        is: true,
        then: Yup.string().required("Este campo é obrigatório"),
        otherwise: Yup.string().notRequired(),
    }),
    maxDosage: Yup.string().when("showConcentration", {
        is: true,
        then: Yup.string().required("Este campo é obrigatório"),
        otherwise: Yup.string().notRequired(),
    }),
    source: Yup.string().required("Este campo é obrigatório"),
    incompatibility: Yup.string().required("Este campo é obrigatório"),
    period: Yup.string().when("showConcentration", {
        is: true,
        then: Yup.string().required("Este campo é obrigatório"),
        otherwise: Yup.string().notRequired(),
    }),
    pregnant: Yup.boolean().required("Este campo é obrigatório"),
    vegan: Yup.boolean().required("Este campo é obrigatório"),
    organic: Yup.boolean().required("Este campo é obrigatório"),
});

/** Estado, formik, fetch e handlers da administração de produtos. */
export function useProductPage() {
    const formik = useFormik({
        initialValues: productViewInitial,
        validationSchema: productSchema,
        onSubmit: () => {
            registerProduct();
        },
    });

    const [subclassDisabled, setSubclassDisabled] = React.useState<boolean>(true);
    const [activeDisabled, setActiveDisabled] = React.useState<boolean>(false);
    const [actives, setActives] = React.useState<ActiveModel[]>([]);
    const [subclasses, setSubclasses] = React.useState<SubclassActiveCompositeModel[]>([]);
    const [rowsPerPage, setRowsPerPage] = React.useState<number>(10);
    const [page, setPage] = React.useState<number>(0);
    const [rowsProducts, setRowsProducts] = React.useState<ProductsWithFilterNPaginationResponse[]>([]);
    const [formOpen, setFormOpen] = useState(false);
    const [open, setOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = React.useState<ProductModel>({} as ProductModel);
    const [openBackdrop, setOpenBackdrop] = useState(false);
    const [productViewModel, setProductViewModel] = React.useState<ProductViewModel>(productViewInitial);
    const [searchProductModel, setSearchProductModel] = React.useState({
        disabledProduct: "AMBOS",
        period: "AMBOS",
    } as SearchProductModel);
    const [btnSaveDisabled, setBtnSaveDisabled] = React.useState<boolean>(false);
    const [dialogModel, setDialogModel] = React.useState<PropsMessageDialog>({
        onConfirm: () => {
            dialogModel.open = false;
            setDialogModel({ ...dialogModel });
        },
        open: false,
    } as PropsMessageDialog);

    const resetForm = () => {
        setProductViewModel({ ...productViewInitial });
        setActiveDisabled(false);
    };

    const closeForm = () => {
        resetForm();
        setFormOpen(false);
    };

    const registerProduct = () => {
        setBtnSaveDisabled(true);
        setOpenBackdrop(true);
        const { showConcentration: _showConcentration, ...productFields } = productViewModel;
        const product: ProductWriteModel = {
            ...productFields,
            subclass: productViewModel.subclass
                ? productViewModel.subclass.map((v) => {
                      return { id: v.idSubclass, name: v.nameSubclass } as SubclassModel;
                  })
                : [],
        };

        saveProduct(product)
            .then(() => {
                const message = product.idProduct == null ? "Ativo cadastrado com sucesso!" : "Ativo alterado com sucesso";
                setDialogModel({ ...messageSuccessConfirmDialog(dialogModel.onConfirm, message) });
                resetForm();
                setFormOpen(false);
                getProductsByPagination(page, rowsPerPage, searchProductModel);
            })
            .catch(() => setDialogModel({ ...messageErrorConfirmDialog(dialogModel.onConfirm) }))
            .finally(() => {
                setOpenBackdrop(false);
                setBtnSaveDisabled(false);
            });
    };

    const updateFormikValues = (formikInstance: any, values: any) => {
        Object.entries(values).map(([key, val]) => (formikInstance.values[key] = val));
    };

    const handleChangePage = async (event: unknown, newPage: number) => {
        setPage(newPage);
        getProductsByPagination(newPage * rowsPerPage, rowsPerPage, searchProductModel);
    };

    const handleChangeRowsPerPage = async (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
        getProductsByPagination(0, +event.target.value, searchProductModel);
    };

    const getProductsByPagination = (
        page: number,
        rowsPerPage: number,
        searchProduct: SearchProductModel,
        callbackFinally?: Function
    ) => {
        getProductsWithFilterNPagination(page, rowsPerPage, searchProduct)
            .then((result) => {
                if (result.status === 200) {
                    setRowsProducts(result.data);
                }
            })
            .catch(() => {
                setDialogModel({ ...messageErrorConfirmDialog(dialogModel.onConfirm) });
            })
            .finally(() => {
                if (callbackFinally != null) callbackFinally();
            });
    };

    const handleClickEnableProduct = (product: ProductsWithFilterNPaginationResponse) => {
        let message = "Ativo desabilitado com sucesso";
        let promise = disableProduct;

        if (product.disabled_Product === 1) {
            message = "Ativo habilitado com sucesso";
            promise = enableProduct;
        }

        promise(product.id_Product.toString())
            .then(() => {
                getProductsByPagination(page, rowsPerPage, searchProductModel);
                setDialogModel({ ...messageSuccessConfirmDialog(dialogModel.onConfirm, message) });
            })
            .catch(() => {
                setDialogModel({ ...messageErrorConfirmDialog(dialogModel.onConfirm) });
            });
    };

    const handleClickViewProduct = (product: ProductsWithFilterNPaginationResponse) => {
        const mappedProduct: ProductModel = {
            idProduct: product.id_Product,
            idBody: 0,
            idTypeUse: 0,
            description: product.description_Product,
            active: [],
            productName: product.name_Product,
            mechanismAction: product.mechanismAction_Product,
            pH: product.pH_Product,
            minimumDosage: product.minimumDosage_Product,
            maxDosage: product.maxDosage_Product,
            source: product.source_Product,
            incompatibility: product.incompatibility_Product,
            pregnant: product.pregnant_Product === 1,
            vegan: product.vegan_Product === 1,
            organic: product.organic_Product === 1,
            period: getDescriptionPeriodByProduct(product),
            daytime: product.daytime_Product,
            nighttime: product.nighttime_Product,
            favorite: product.favorite,
            classes: product.classes,
        };
        setSelectedProduct(mappedProduct);
        setOpen(true);
    };

    const handleClickEditProduct = (product: ProductsWithFilterNPaginationResponse) => {
        setOpenBackdrop(true);

        Promise.all([getActiveProducts(product.id_Product.toString()), getSubclassProducts(product.id_Product.toString())]).then(
            (response) => {
                if (response[0].status !== 200 || response[1].status !== 200) {
                    setDialogModel({ ...messageErrorConfirmDialog(dialogModel.onConfirm) });
                    return;
                }

                productViewModel.active = response[0].data.activeModel;
                productViewModel.subclass = response[1].data.subclassModel;
                productViewModel.productName = product.name_Product;
                productViewModel.phProduct = product.pH_Product;
                productViewModel.minimumDosage = product.minimumDosage_Product;
                productViewModel.maxDosage = product.maxDosage_Product;
                productViewModel.incompatibility = product.incompatibility_Product;
                productViewModel.source = product.source_Product;
                productViewModel.mechanismAction = product.mechanismAction_Product;
                productViewModel.description = product.description_Product;
                productViewModel.pregnant = product.pregnant_Product === 1;
                productViewModel.organic = product.organic_Product === 1;
                productViewModel.vegan = product.vegan_Product === 1;
                productViewModel.idProduct = product.id_Product;
                productViewModel.period = getDescriptionPeriodByProduct(product);
                productViewModel.showConcentration =
                    productViewModel.active.some((active) => active.showConcentration) ||
                    productViewModel.minimumDosage !== "" ||
                    productViewModel.maxDosage !== "";

                setActiveDisabled(true);
                setProductViewModel({ ...productViewModel });
                setOpenBackdrop(false);
                setFormOpen(true);
            }
        );
    };

    const searchProducts = () => {
        setOpenBackdrop(true);
        getProductsByPagination(page, rowsPerPage, searchProductModel, () => setOpenBackdrop(false));
    };

    const openCreate = () => {
        resetForm();
        setFormOpen(true);
    };

    useEffect(() => {
        getAllActives()
            .then((result) => {
                if (result.status === 200) {
                    setActives(result.data.data);
                }
            })
            .catch(() => {
                setDialogModel({ ...messageErrorConfirmDialog(dialogModel.onConfirm) });
            });

        searchProducts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (productViewModel.active != null && productViewModel.active.length > 0) {
            const idsActives = productViewModel.active
                .filter((v) => v.id != null)
                .map((v) => v.id)
                .join(",");

            getSubclassByActive(idsActives)
                .then((result) => {
                    if (result.status === 200) {
                        setSubclasses(result.data.data);
                    }
                })
                .catch(() => {
                    setDialogModel({ ...messageErrorConfirmDialog(dialogModel.onConfirm) });
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productViewModel.active]);

    useEffect(() => {
        updateFormikValues(formik, productViewModel);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productViewModel]);

    return {
        formik,
        subclassDisabled,
        setSubclassDisabled,
        activeDisabled,
        actives,
        subclasses,
        rowsPerPage,
        page,
        rowsProducts,
        formOpen,
        open,
        setOpen,
        selectedProduct,
        openBackdrop,
        productViewModel,
        setProductViewModel,
        searchProductModel,
        setSearchProductModel,
        btnSaveDisabled,
        dialogModel,
        closeForm,
        handleChangePage,
        handleChangeRowsPerPage,
        handleClickEnableProduct,
        handleClickViewProduct,
        handleClickEditProduct,
        searchProducts,
        openCreate,
    };
}
